import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    Loader2, Search, MapPin, ArrowLeft, CheckCircle2, XCircle, ChevronDown,
    ChevronUp, Star, MessageCircle, Users2, GitCompare, ShieldCheck,
    Briefcase, DollarSign, AlertTriangle, EyeOff, SlidersHorizontal,
    Zap, ListChecks,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import MessageComposeDialog from "@/components/messaging/MessageComposeDialog";
import CompareDialog from "@/components/employer/CompareDialog";

/**
 * iter-168 — Per-role Browse Talent view (spec §4-§6).
 *
 * Strict rules enforced here:
 *   - NO scores / percentages / stars anywhere. Match line only.
 *   - Knockout-failing passports stay visible in the collapsed "Didn't meet
 *     filters" section — never auto-rejected.
 *   - First name + last initial only (full name after talent replies).
 */
const BrowseTalent = () => {
    const { jobId } = useParams();
    const [filters, setFilters] = useState({
        skills_mode: "any", verification: "all", availability: "all",
    });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCollapsed, setShowCollapsed] = useState(false);
    const [shortlist, setShortlist] = useState([]);
    const [messageTarget, setMessageTarget] = useState(null);
    const [compareOpen, setCompareOpen] = useState(false);
    const [selectedForCompare, setSelectedForCompare] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [match, sl] = await Promise.all([
                api.get(`/employer/jobs/${jobId}/match-browse`, { params: filters }),
                api.get(`/employer/jobs/${jobId}/shortlist`),
            ]);
            setData(match.data);
            setShortlist(sl.data.shortlist || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [jobId, filters]);

    useEffect(() => { load(); }, [load]);

    const shortlistedIds = useMemo(
        () => new Set(shortlist.map((s) => s.talent_user_id)), [shortlist]);

    const toggleShortlist = async (row) => {
        if (shortlistedIds.has(row.talent_user_id)) {
            try {
                await api.delete(`/employer/jobs/${jobId}/shortlist/${row.talent_user_id}`);
                toast.success("Removed from shortlist.");
                load();
            } catch (e) { toast.error(formatApiError(e)); }
        } else {
            try {
                await api.post(`/employer/jobs/${jobId}/shortlist`,
                               { talent_user_id: row.talent_user_id });
                toast.success("Added to shortlist.");
                load();
            } catch (e) { toast.error(formatApiError(e)); }
        }
    };

    const toggleCompareSelect = (uid) => {
        setSelectedForCompare((prev) => {
            if (prev.includes(uid)) return prev.filter((u) => u !== uid);
            if (prev.length >= 3) {
                toast.error("Compare up to 3");
                return prev;
            }
            return [...prev, uid];
        });
    };

    const moveToPipeline = async () => {
        if (shortlist.length === 0) { toast.error("Shortlist is empty."); return; }
        try {
            const { data: resp } = await api.post(
                `/employer/jobs/${jobId}/move-to-pipeline`,
                { talent_user_ids: shortlist.map((s) => s.talent_user_id) });
            const moved = resp.results.filter((r) => r.outcome === "moved").length;
            const already = resp.results.filter((r) => r.outcome === "already_in_pipeline").length;
            toast.success(`Moved ${moved} candidate${moved === 1 ? "" : "s"} to pipeline${already ? ` (${already} already there)` : ""}.`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading && !data) {
        return <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-emerald-500" /></div>;
    }
    if (!data) return null;

    return (
        <TooltipProvider delayDuration={120}>
            <div className="max-w-7xl mx-auto px-4 py-6" data-testid="browse-talent-page">
                <Link
                    to="/employer/jobs"
                    data-testid="browse-back"
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3"
                >
                    <ArrowLeft size={11} /> Back to roles
                </Link>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                            <Users2 size={16} /> <p className="overline">Browse talent</p>
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-foreground mt-1 truncate">
                            {data.role.title}
                        </h1>
                        <div className="mt-1 text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                            <span className="inline-flex items-center gap-1"><MapPin size={11} />{data.role.city}, {data.role.state}</span>
                            <span className="inline-flex items-center gap-1"><Briefcase size={11} />{(data.role.remote_status || "on_site").replace("_", " ")}</span>
                            <span className="inline-flex items-center gap-1"><DollarSign size={11} />${(data.role.salary_min / 1000).toFixed(0)}k–${(data.role.salary_max / 1000).toFixed(0)}k</span>
                            {(data.role.required_skills || []).map((s) => (
                                <Badge key={s} className="bg-muted text-foreground border border-border">{s}</Badge>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            onClick={moveToPipeline}
                            disabled={shortlist.length === 0}
                            data-testid="browse-move-pipeline"
                            className="btn-green text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
                            title="Move the shortlist into the ATS pipeline"
                        >
                            <Zap size={11} /> Move {shortlist.length || ""} to pipeline
                        </button>
                        <button
                            onClick={() => setCompareOpen(true)}
                            disabled={selectedForCompare.length < 2}
                            data-testid="browse-open-compare"
                            className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
                            title={selectedForCompare.length < 2 ? "Pick 2–3 to compare" : "Compare side-by-side"}
                        >
                            <GitCompare size={11} /> Compare ({selectedForCompare.length})
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="mt-5 card-soft p-4 flex items-center gap-3 flex-wrap" data-testid="browse-filters">
                    <SlidersHorizontal size={14} className="text-muted-foreground" />
                    <FilterField label="Skills" testId="filter-skills-mode">
                        <Select value={filters.skills_mode} onValueChange={(v) => setFilters((f) => ({ ...f, skills_mode: v }))}>
                            <SelectTrigger className="h-8 text-xs w-32" data-testid="filter-skills-mode-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">Match any skill</SelectItem>
                                <SelectItem value="all">Match all skills</SelectItem>
                            </SelectContent>
                        </Select>
                    </FilterField>
                    <FilterField label="Verification">
                        <Select value={filters.verification} onValueChange={(v) => setFilters((f) => ({ ...f, verification: v }))}>
                            <SelectTrigger className="h-8 text-xs w-44" data-testid="filter-verification"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All verified</SelectItem>
                                <SelectItem value="tier0">Tier 0+ (phone + attested)</SelectItem>
                                <SelectItem value="tier1">Tier 1 ID-verified only</SelectItem>
                            </SelectContent>
                        </Select>
                    </FilterField>
                    <FilterField label="Availability">
                        <Select value={filters.availability} onValueChange={(v) => setFilters((f) => ({ ...f, availability: v }))}>
                            <SelectTrigger className="h-8 text-xs w-44" data-testid="filter-availability"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="actively_looking">Actively looking first</SelectItem>
                            </SelectContent>
                        </Select>
                    </FilterField>
                    {loading && <Loader2 size={14} className="animate-spin text-emerald-500" />}
                </div>

                {/* Primary results */}
                <div className="mt-5">
                    <h2 className="text-sm font-heading font-semibold text-foreground mb-3">
                        {data.primary.length} match{data.primary.length === 1 ? "" : "es"}
                    </h2>
                    {data.primary.length === 0 ? (
                        <div className="card-soft p-10 text-center text-sm text-muted-foreground" data-testid="browse-empty-primary">
                            No candidates met all filters. Expand "Didn't meet filters" below — nothing is hidden; the final call is always yours.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {data.primary.map((row) => (
                                <CandidateCard
                                    key={row.talent_user_id} row={row}
                                    role={data.role}
                                    matchingEnabled={!!data.matching_prod_enabled}
                                    shortlisted={shortlistedIds.has(row.talent_user_id)}
                                    selectedForCompare={selectedForCompare.includes(row.talent_user_id)}
                                    onShortlist={() => toggleShortlist(row)}
                                    onCompareToggle={() => toggleCompareSelect(row.talent_user_id)}
                                    onMessage={() => setMessageTarget(row)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Collapsed bucket */}
                {data.collapsed.length > 0 && (
                    <div className="mt-6">
                        <button
                            onClick={() => setShowCollapsed((v) => !v)}
                            data-testid="browse-collapsed-toggle"
                            className="w-full flex items-center justify-between card-soft p-3 text-left hover:bg-muted/30"
                        >
                            <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
                                <EyeOff size={14} className="text-amber-500" />
                                Didn&apos;t meet filters ({data.collapsed.length})
                            </span>
                            {showCollapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        {showCollapsed && (
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="browse-collapsed-grid">
                                {data.collapsed.map((row) => (
                                    <CandidateCard
                                        key={row.talent_user_id} row={row}
                                        role={data.role} collapsed
                                        matchingEnabled={!!data.matching_prod_enabled}
                                        shortlisted={shortlistedIds.has(row.talent_user_id)}
                                        selectedForCompare={selectedForCompare.includes(row.talent_user_id)}
                                        onShortlist={() => toggleShortlist(row)}
                                        onCompareToggle={() => toggleCompareSelect(row.talent_user_id)}
                                        onMessage={() => setMessageTarget(row)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <MessageComposeDialog
                    open={!!messageTarget}
                    onOpenChange={(o) => !o && setMessageTarget(null)}
                    talentUserId={messageTarget?.talent_user_id}
                    talentLabel={messageTarget?.display_name || "this candidate"}
                    jobId={jobId}
                />
                <CompareDialog
                    open={compareOpen}
                    onOpenChange={setCompareOpen}
                    jobId={jobId}
                    talentUserIds={selectedForCompare}
                />
            </div>
        </TooltipProvider>
    );
};

const FilterField = ({ label, children }) => (
    <div className="inline-flex items-center gap-1.5">
        <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</label>
        {children}
    </div>
);

const CandidateCard = ({ row, role, collapsed = false, shortlisted, selectedForCompare,
                        onShortlist, onCompareToggle, onMessage, matchingEnabled }) => {
    const copy = row.verification_copy || {};
    const isTier1 = row.verification_tier >= 1;
    return (
        <div
            data-testid={`candidate-${row.talent_user_id}`}
            className={`card-soft p-4 flex flex-col gap-2 ${collapsed ? "opacity-80" : ""}`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-foreground">
                        {row.display_name}
                        {" "}
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span
                                    data-testid={`vbadge-${row.talent_user_id}`}
                                    className={`inline-flex items-center gap-1 ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold border ${
                                        isTier1
                                            ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-400"
                                            : "bg-sky-500/15 border-sky-500/40 text-sky-700 dark:text-sky-400"
                                    }`}
                                >
                                    <ShieldCheck size={9} /> {copy.badge}
                                </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs">{copy.tooltip}</TooltipContent>
                        </Tooltip>
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                        {(row.city || row.state) && (
                            <span className="inline-flex items-center gap-0.5"><MapPin size={10} />{row.city}{row.city && row.state ? ", " : ""}{row.state}</span>
                        )}
                        {row.work_type_preference && (
                            <span className="inline-flex items-center gap-0.5"><Briefcase size={10} />{row.work_type_preference.replace("_", " ")}</span>
                        )}
                        {row.availability_status === "actively_looking" && (
                            <span className="inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400"><Zap size={10} />Actively looking</span>
                        )}
                    </div>
                </div>
                <label className="inline-flex items-center gap-1 text-[11px] text-muted-foreground cursor-pointer shrink-0" title="Select for compare">
                    <input
                        type="checkbox"
                        checked={selectedForCompare}
                        onChange={onCompareToggle}
                        data-testid={`compare-pick-${row.talent_user_id}`}
                        className="rounded"
                    />
                    Compare
                </label>
            </div>

            {row.top_skills?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {row.top_skills.map((s) => (
                        <Badge key={s} className="bg-muted text-foreground border border-border text-[10px]">{s}</Badge>
                    ))}
                </div>
            )}

            <div className="rounded-lg bg-muted/40 border border-border px-3 py-2 text-xs" data-testid={`match-summary-${row.talent_user_id}`}>
                {matchingEnabled ? (
                    <>
                        <div className="font-semibold text-foreground">{row.match_summary}</div>
                        {row.missing_summary && (
                            <div className="mt-0.5 text-muted-foreground text-[11px]">{row.missing_summary}</div>
                        )}
                        <details className="mt-1.5">
                            <summary className="text-[11px] text-emerald-700 dark:text-emerald-400 cursor-pointer select-none" data-testid={`match-expand-${row.talent_user_id}`}>
                                View factor breakdown
                            </summary>
                            <ul className="mt-2 space-y-1">
                                {row.factors.map((f) => (
                                    <li key={f.key} data-testid={`factor-${row.talent_user_id}-${f.key}`} className="flex items-center gap-1.5 text-[11px]">
                                        {f.met
                                            ? <CheckCircle2 size={11} className="text-emerald-500 shrink-0" />
                                            : <XCircle size={11} className="text-rose-500 shrink-0" />}
                                        <span className={f.met ? "text-foreground" : "text-muted-foreground line-through"}>{f.label}</span>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    </>
                ) : (
                    <div className="text-[11px] text-muted-foreground" data-testid={`match-gated-${row.talent_user_id}`}>
                        Full match breakdown available in preview — pending legal review.
                    </div>
                )}
            </div>

            <div className="mt-auto pt-1 flex items-center justify-end gap-1.5">
                <button
                    onClick={onShortlist}
                    data-testid={`shortlist-btn-${row.talent_user_id}`}
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                        shortlisted
                            ? "bg-amber-500/15 border border-amber-500/40 text-amber-800 dark:text-amber-300"
                            : "border border-border bg-card text-foreground hover:bg-muted/40"
                    }`}
                >
                    <Star size={10} className={shortlisted ? "fill-current" : ""} />
                    {shortlisted ? "Shortlisted" : "Shortlist"}
                </button>
                <button
                    onClick={onMessage}
                    data-testid={`msg-btn-${row.talent_user_id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15"
                >
                    <MessageCircle size={10} /> Message
                </button>
            </div>
            {collapsed && row.failed_filters?.length > 0 && (
                <div className="mt-1 text-[10px] text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
                    <AlertTriangle size={10} /> Missed: {row.failed_filters.join(", ")}
                </div>
            )}
        </div>
    );
};

export default BrowseTalent;
